<template>
	<v-menu v-model="isFilterOpen" :offset="4" :close-on-content-click="false" max-width="300px" content-class="menu-content">
		<template #activator="{ props }">
			<slot name="button" :props="props"> View </slot>
		</template>
		<v-card class="menu-card search-filter" rounded="lg" elevation="8" @click.stop>
			<v-list>
				<v-list-item
					v-for="status in sortedStatuses"
					:key="status"
					class="w-100"
					:class="{ 'selected-row': isSelected(status) }"
					@click.stop="toggleSelected(status)">
					<template v-slot:prepend>
						<v-list-item-action start>
							<v-checkbox-btn :model-value="isSelected(status)"></v-checkbox-btn>
						</v-list-item-action>
					</template>
					<v-list-item-title>
						<v-chip class="font-weight-bold py-1 px-2" :class="getStatusClass(status)" label>
							{{ translateStatus(status) }}
						</v-chip>
					</v-list-item-title>
				</v-list-item>
			</v-list>
			<div class="opacity-50">
				<hr />
			</div>
			<v-card-actions class="d-flex justify-end">
				<v-btn
					color="grey"
					text
					rounded="xl"
					elevation="0"
					class="white-bg"
					@click="clear(() => emit('filter-status', []), true)"
					>Clear</v-btn
				>
				<PrimaryBtn
					@click="
						applyFilter(() =>
							emit(
								'filter-status',
								appliedItems.map((i) => i.id)
							)
						)
					"
					>Apply</PrimaryBtn
				>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script lang="ts" setup>
	import { computed, onMounted, watch } from "vue";
	import { translateConversationStatus, translateProjectStatus } from "@/helpers/status";
	import constants from "@/api/constants";
	import { sortStatusesBySelected } from "@/helpers/filters";
	import { useFilterMenu } from "@/composables/useFilterMenu";

	// StatusFilter items are plain strings; we wrap them in a { id: string } adapter
	// to satisfy useFilterMenu's generic constraint.
	type StatusItem = { id: string };

	function toItem(status: string): StatusItem {
		return { id: status };
	}

	const props = defineProps<{
		preselectedStatuses: string[];
		parentType: string;
	}>();

	const emit = defineEmits<{
		(e: "filter-status", statuses: string[]): void;
	}>();

	const {
		isFilterOpen,
		selectedItems,
		appliedItems,
		isSelected: isSelectedItem,
		toggleSelected: toggleSelectedItem,
		applyFilter,
		clear
	} = useFilterMenu<StatusItem>();

	// String-friendly wrappers for the template
	const isSelected = (status: string) => isSelectedItem(toItem(status));
	const toggleSelected = (status: string) => toggleSelectedItem(toItem(status));

	defineExpose({
		clear: (emitEvent: boolean = false) => clear(() => emit("filter-status", []), emitEvent),
		isFilterOpen
	});

	const sortedStatuses = computed(() => {
		return sortStatusesBySelected(
			getStatuses(),
			appliedItems.value.map((i) => i.id)
		);
	});

	watch(
		() => props.preselectedStatuses,
		(newVal) => {
			const valid = [...newVal].filter((x) => getStatuses().includes(x));
			appliedItems.value = valid.map(toItem);
			selectedItems.value = valid.map(toItem);
		}
	);

	onMounted(() => {
		const valid = [...props.preselectedStatuses].filter((x) => getStatuses().includes(x));
		appliedItems.value = valid.map(toItem);
		selectedItems.value = valid.map(toItem);
	});

	function getStatuses(): Array<string> {
		if (props.parentType === "conversations") {
			return Object.keys(constants.CONVERSATION_STATUS).filter(
				(x) =>
					x != constants.CONVERSATION_STATUS.NotStarted &&
					x != constants.CONVERSATION_STATUS.Unread &&
					x != constants.CONVERSATION_STATUS.Delivered
			);
		} else if (props.parentType === "projects") {
			return Object.keys(constants.PROJECT_STATUS);
		}

		const statuses: Array<string> = Object.keys(constants.FILTER_STATUS);
		return statuses;
	}

	function translateStatus(status: string): string {
		if (props.parentType === "conversation") {
			return translateConversationStatus(status, true);
		} else if (props.parentType === "project") {
			return translateProjectStatus(status);
		} else {
			let newStatus = translateConversationStatus(status, true);
			if (newStatus === "" || newStatus === status) {
				newStatus = translateProjectStatus(status);
			}
			return newStatus;
		}
	}

	function getStatusClass(status: string): string {
		return `status-${status.toLowerCase()}`;
	}
</script>

<style scoped lang="scss">
	.menu-content {
		width: 100%;
		max-width: 300px;
	}

	.menu-card {
		width: 100%;
		box-sizing: border-box;
	}

	.status-sent,
	.status-scheduled {
		background-color: #f3f5f9 !important;
		color: #5e5c6e !important;
	}

	.status-inprogress,
	.status-processing,
	.status-inprogress {
		background-color: #ffb80020 !important;
		color: #e9a800 !important;
	}

	.status-readyforreview {
		background-color: #b1d8ff !important;
		color: rgb(var(--v-theme-brand)) !important;
	}

	.status-reviewed,
	.status-completed {
		background-color: #02b55520 !important;
		color: #02b555 !important;
	}

	.status-notstarted {
		background-color: #f3f5f9 !important;
		color: #5e5c6e !important;
	}
</style>
